/**
 * データの取り出し（issue #114・docs/01 15-1「データの一括持ち出し（エクスポート）」・16-4）。
 *
 * 会社のデータ（トーク・メッセージ・名簿・AI先輩の答え・写真とファイル）を、まとめて1つのファイルにして
 * 取り出します。**1対1のやりとりは入りません**（docs/01 15-3）。
 *
 * この画面でできること（1つだけ・docs/06 原則3）:
 *  - 取り出す（押すと予約が入り、裏で作ります）→ できあがったら一覧の「受け取る」から受け取る
 *
 * 決めごと:
 *  - 使えるのは「会社のデータをまとめて取り出す」を持つ方（初期値では本部管理者だけ）。できる・できないを
 *    決めるのはサーバの門番です（左の項目・この画面の出し分けは見せ方だけ）
 *  - 取り出す前に確認の窓をはさみ、押したあとは結果を出す（決定22・docs/06 原則1・4）
 *  - 状態は言葉で出す（順番待ち・作っています・受け取れます・作れませんでした・期限が過ぎたため消しました）。
 *    作っている途中は10秒ごとに読み直す
 *  - 取り出したこと・受け取ったことは、誰がいつ行ったかが操作履歴に残る
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import type { DataExportDownload, DataExportItem, DataExportPage } from '../api/types.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { startDownload } from '../exports/download.js';
import {
  exportContentLabel,
  exportStatusDetail,
  exportStatusLabel,
  exportStatusTone,
  formatBytes,
  isExportActive,
} from '../exports/labels.js';
import { formatTableDateTime } from '../format/datetime.js';
import { withReason } from '../format/sentence.js';

/** 作っている途中に読み直す間隔（ミリ秒）。 */
export const DATA_EXPORT_REFRESH_MS = 10_000;

/** 一覧に出す件数（過去の実行は多くならないので、ページ送りは付けない）。 */
const LIST_LIMIT = 20;

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

export function DataExportsPage(): ReactNode {
  const api = useApi();
  const [page, setPage] = useState<DataExportPage | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [format, setFormat] = useState<string | null>(null);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const loaded = await api.get<DataExportPage>('/admin/data-exports', {
        query: { limit: LIST_LIMIT },
      });
      setPage(loaded);
      setError(null);
    } catch (caught: unknown) {
      setError(loadFailure(caught));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  // 作っている途中のものがある間だけ、10秒ごとに読み直す（できあがったら「受け取れます」に変わる）
  const active = page?.items.some(isExportActive) ?? false;
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => void load(), DATA_EXPORT_REFRESH_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  const chosenFormat = format ?? page?.default_format ?? null;
  const chosenLabel = page?.formats.find((f) => f.key === chosenFormat)?.label ?? '';

  async function requestExport(): Promise<void> {
    setBusy(true);
    setBanner(null);
    try {
      await api.post<DataExportItem>('/admin/data-exports', {
        body: {
          ...(chosenFormat ? { format: chosenFormat } : {}),
          include_files: includeFiles,
        },
      });
      setConfirming(false);
      setBanner({
        tone: 'success',
        message:
          '取り出しを受け付けました。まもなく作り始めます。できあがると、下の一覧の状態が「受け取れます」に変わります。',
        nextStep: 'この画面を閉じても、作るのは止まりません。',
      });
      await load();
    } catch (caught: unknown) {
      setConfirming(false);
      setBanner({
        tone: 'error',
        message: withReason('取り出せませんでした', toMessage(caught)),
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function download(item: DataExportItem): Promise<void> {
    setDownloading(item.id);
    setBanner(null);
    try {
      const link = await api.post<DataExportDownload>(
        `/admin/data-exports/${encodeURIComponent(item.id)}/download`,
      );
      startDownload(link.url);
      setBanner({
        tone: 'success',
        message: `受け取りを始めました（${link.filename}）。受け取ったことは操作履歴に残ります。`,
        nextStep:
          'ブラウザの「ダウンロード」をご確認ください。中身には個人情報が入っています。共有のフォルダに置いたり、メールで送ったりしないでください。',
      });
      await load();
    } catch (caught: unknown) {
      setBanner({
        tone: 'error',
        message: withReason('受け取れませんでした', toMessage(caught)),
      });
      await load();
    } finally {
      setDownloading(null);
    }
  }

  return (
    <article>
      <PageHeader
        title="データの取り出し"
        description="会社のデータ（トーク・メッセージ・メンバー・AI先輩の答え・写真とファイル）を、まとめて1つのファイルにして取り出します。1対1のやりとりは入りません。"
      />

      {banner ? (
        <ResultBanner
          reveal
          tone={banner.tone}
          message={banner.message}
          {...(banner.nextStep !== undefined ? { nextStep: banner.nextStep } : {})}
        />
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !page ? <LoadingBlock /> : null}

      {page && active ? (
        <Card title="取り出す">
          <p className="admin-empty">
            いま作っている取り出しがあります。できあがると、下の一覧から受け取れます。次の取り出しは、できあがってから押せます。
          </p>
        </Card>
      ) : null}

      {page && !active ? (
        <FormCard
          title="取り出す"
          description={`押すと作り始めます。できあがるまで、数分から、写真が多いときは数時間かかります。できあがってから${page.keep_hours}時間は受け取れます。過ぎるとファイルは消えます。`}
          submitLabel="取り出す"
          busy={busy}
          onSubmit={() => setConfirming(true)}
        >
          <fieldset className="admin-choice-group">
            <legend>形式</legend>
            {page.formats.map((option) => (
              <label key={option.key} className="admin-choice">
                <input
                  type="radio"
                  name="data-export-format"
                  value={option.key}
                  checked={chosenFormat === option.key}
                  onChange={() => setFormat(option.key)}
                />
                <span className="admin-choice-body">
                  <strong>{option.label}</strong>
                  <span className="admin-choice-detail">{option.note}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="ui-check" htmlFor="data-export-files">
            <input
              id="data-export-files"
              type="checkbox"
              checked={includeFiles}
              onChange={(event) => setIncludeFiles(event.target.checked)}
            />
            <span>写真・ファイルの中身も入れる（大きくなり、できあがるまで時間がかかります）</span>
          </label>
        </FormCard>
      ) : null}

      {page ? (
        <Card
          title="これまでの取り出し"
          description="新しい順です。受け取れるのは「受け取れます」になっているものだけです。"
          actions={
            <Button variant="secondary" compact onClick={() => setReloadKey((n) => n + 1)}>
              読み直す
            </Button>
          }
        >
          {page.items.length === 0 ? (
            <EmptyBlock label="まだ取り出したことはありません。" />
          ) : (
            <DataTable label="これまでの取り出し">
              <thead>
                <tr>
                  <th scope="col">押した日時</th>
                  <th scope="col">押した方</th>
                  <th scope="col">中身</th>
                  <th scope="col">状態</th>
                  <th scope="col">大きさ</th>
                  <th scope="col">
                    <span className="admin-visually-hidden">受け取る</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((item) => (
                  <tr key={item.id}>
                    <th scope="row">{formatTableDateTime(item.requested_at)}</th>
                    <td>{item.requested_by_name ?? '―'}</td>
                    <td className="ui-cell-wrap">{exportContentLabel(item)}</td>
                    <td className="ui-cell-wrap">
                      <span className={`admin-tag admin-tag-${exportStatusTone(item.status)}`}>
                        {exportStatusLabel(item.status)}
                      </span>
                      <span className="admin-choice-detail">{exportStatusDetail(item)}</span>
                    </td>
                    <td className="ui-cell-num">
                      {item.status === 'ready' ? formatBytes(item.size_bytes) : '―'}
                    </td>
                    <td>
                      {item.status === 'ready' ? (
                        <Button
                          variant="secondary"
                          compact
                          busy={downloading === item.id}
                          busyLabel="用意しています…"
                          disabled={downloading !== null}
                          onClick={() => void download(item)}
                        >
                          受け取る
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirming}
        title="会社のデータを取り出します"
        message={`${chosenLabel}で、トーク・メッセージ・メンバー（電話番号を含みます）・AI先輩の答え${includeFiles ? '・写真とファイル' : ''}をまとめます。個人情報が入ったファイルになります。1対1のやりとりは入りません。`}
        reversibility="取り出したことと受け取ったことは、誰がいつ行ったかが操作履歴に残ります。"
        reversible
        tone="primary"
        confirmLabel="取り出す"
        busy={busy}
        onConfirm={() => void requestExport()}
        onCancel={() => setConfirming(false)}
      />
    </article>
  );
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
